import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useRoute } from 'wouter';
import { CheckCircle2, FileText } from 'lucide-react';

import { PublicShell } from '@/components/public-shell';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { fetchJson } from '@/lib/queryClient';
import { formatDateTimeRange } from '@/lib/dateUtils';

interface WaiverPayload {
  status: 'pending' | 'signed';
  signerName: string | null;
  signerEmail: string | null;
  signerPhone: string | null;
  emergencyContactName: string | null;
  emergencyContactPhone: string | null;
  ageConfirmed: boolean;
  mediaReleaseConsent: boolean | null;
  waiverTitle: string;
  waiverText: string;
  waiverVersion: number;
  signedAt: string | null;
  expired: boolean;
  classTitle: string;
  sessionStart: string | null;
  sessionEnd: string | null;
}

export default function WaiverSign() {
  const [, params] = useRoute('/waivers/sign/:token');
  const token = params?.token || '';
  const { toast } = useToast();
  const [signerName, setSignerName] = useState('');
  const [signerEmail, setSignerEmail] = useState('');
  const [signerPhone, setSignerPhone] = useState('');
  const [emergencyContactName, setEmergencyContactName] = useState('');
  const [emergencyContactPhone, setEmergencyContactPhone] = useState('');
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [mediaReleaseConsent, setMediaReleaseConsent] = useState<boolean | null>(null);
  const [signatureDate, setSignatureDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [accepted, setAccepted] = useState(false);

  const waiverQuery = useQuery<WaiverPayload>({
    queryKey: ['/api/waivers/sign', token],
    enabled: Boolean(token),
    queryFn: () => fetchJson<WaiverPayload>(`/api/waivers/sign/${encodeURIComponent(token)}`),
  });

  const payload = waiverQuery.data;
  const defaultName = payload?.signerName || '';
  const effectiveSignerName = signerName || defaultName;
  const effectiveSignerEmail = signerEmail || payload?.signerEmail || '';
  const effectiveSignerPhone = signerPhone || payload?.signerPhone || '';
  const effectiveEmergencyContactName =
    emergencyContactName || payload?.emergencyContactName || '';
  const effectiveEmergencyContactPhone =
    emergencyContactPhone || payload?.emergencyContactPhone || '';
  const sessionLabel =
    payload?.sessionStart && payload?.sessionEnd
      ? formatDateTimeRange(payload.sessionStart, payload.sessionEnd)
      : null;

  const paragraphs = useMemo(
    () =>
      (payload?.waiverText || '')
        .split(/\n{2,}/)
        .map(part => part.trim())
        .filter(Boolean),
    [payload?.waiverText]
  );

  const signMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`/api/waivers/sign/${encodeURIComponent(token)}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          signerName: effectiveSignerName,
          signerEmail: effectiveSignerEmail,
          signerPhone: effectiveSignerPhone,
          emergencyContactName: effectiveEmergencyContactName,
          emergencyContactPhone: effectiveEmergencyContactPhone,
          ageConfirmed,
          mediaReleaseConsent,
          signatureDate,
        }),
      });
      if (!res.ok) {
        const text = (await res.text()) || res.statusText;
        throw new Error(text);
      }
      return res.json();
    },
    onSuccess: () => {
      toast({ title: 'Waiver signed', description: 'Your waiver has been recorded.' });
      void waiverQuery.refetch();
    },
    onError: () => {
      toast({
        title: 'Could not sign waiver',
        description: 'Please check the form and try again.',
        variant: 'destructive',
      });
    },
  });

  return (
    <PublicShell routeKey="classes">
      <div className="page-shell py-8">
        <Card className="mx-auto max-w-3xl">
          <CardHeader>
            <div className="flex items-center gap-3">
              <FileText className="h-6 w-6 text-primary" />
              <div>
                <CardTitle>{payload?.waiverTitle || 'Class Waiver'}</CardTitle>
                <CardDescription>
                  {payload
                    ? sessionLabel
                      ? `${payload.classTitle} - ${sessionLabel}`
                      : payload.classTitle
                    : 'Review and sign your class waiver.'}
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {waiverQuery.isLoading ? (
              <div className="flex justify-center py-10">
                <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              </div>
            ) : waiverQuery.isError ? (
              <div className="rounded-md border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
                This waiver link could not be found. Ask your instructor to send a new waiver
                request.
              </div>
            ) : payload?.expired ? (
              <div className="rounded-md border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
                This waiver link has expired. Ask your instructor to send a new waiver request.
              </div>
            ) : payload?.status === 'signed' ? (
              <div className="flex items-start gap-3 rounded-md border border-green-300 bg-green-50 p-4 text-green-900">
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" />
                <div>
                  <p className="font-medium">Waiver already signed</p>
                  <p className="text-sm">
                    Signed by {payload.signerName || 'attendee'}
                    {payload.signedAt ? ` on ${new Date(payload.signedAt).toLocaleString()}` : ''}.
                  </p>
                </div>
              </div>
            ) : (
              <>
                <div className="max-h-[48vh] overflow-y-auto rounded-md border bg-muted/30 p-4 text-sm leading-7">
                  {paragraphs.length > 0 ? (
                    paragraphs.map((paragraph, index) => <p key={index}>{paragraph}</p>)
                  ) : (
                    <p>No waiver text was provided for this class.</p>
                  )}
                </div>
                <div className="space-y-3">
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                      <Label htmlFor="signer-name">Full name</Label>
                      <Input
                        id="signer-name"
                        value={effectiveSignerName}
                        onChange={event => setSignerName(event.target.value)}
                        placeholder="Type your full legal name"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="signer-email">Email address</Label>
                      <Input
                        id="signer-email"
                        value={effectiveSignerEmail}
                        onChange={event => setSignerEmail(event.target.value)}
                        placeholder="Email address"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="signer-phone">Phone number</Label>
                      <Input
                        id="signer-phone"
                        value={effectiveSignerPhone}
                        onChange={event => setSignerPhone(event.target.value)}
                        placeholder="Phone number"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="signature-date">Date of signature</Label>
                      <Input
                        id="signature-date"
                        type="date"
                        value={signatureDate}
                        onChange={event => setSignatureDate(event.target.value)}
                        required
                      />
                    </div>
                  </div>

                  <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                      <Label htmlFor="emergency-contact-name">Emergency contact name</Label>
                      <Input
                        id="emergency-contact-name"
                        value={effectiveEmergencyContactName}
                        onChange={event => setEmergencyContactName(event.target.value)}
                        placeholder="Emergency contact"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="emergency-contact-phone">Emergency contact phone</Label>
                      <Input
                        id="emergency-contact-phone"
                        value={effectiveEmergencyContactPhone}
                        onChange={event => setEmergencyContactPhone(event.target.value)}
                        placeholder="Emergency contact phone"
                        required
                      />
                    </div>
                  </div>

                  <label className="flex items-start gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={ageConfirmed}
                      onChange={event => setAgeConfirmed(event.target.checked)}
                      className="mt-1 h-4 w-4"
                    />
                    <span>
                      I confirm that I am 18 years of age or older, or have obtained
                      parental/guardian consent to participate.
                    </span>
                  </label>

                  <div className="space-y-2 text-sm">
                    <p className="font-medium">Media release preference</p>
                    <label className="flex items-start gap-2">
                      <input
                        type="radio"
                        name="media-release"
                        checked={mediaReleaseConsent === true}
                        onChange={() => setMediaReleaseConsent(true)}
                        className="mt-1 h-4 w-4"
                      />
                      <span>
                        I consent to photos or videos taken during class being used for promotional
                        purposes.
                      </span>
                    </label>
                    <label className="flex items-start gap-2">
                      <input
                        type="radio"
                        name="media-release"
                        checked={mediaReleaseConsent === false}
                        onChange={() => setMediaReleaseConsent(false)}
                        className="mt-1 h-4 w-4"
                      />
                      <span>
                        I do not consent to the use of my image or likeness for promotional
                        purposes.
                      </span>
                    </label>
                  </div>

                  <div>
                    <Label htmlFor="digital-signature">Digital signature</Label>
                    <Input
                      id="digital-signature"
                      value={effectiveSignerName}
                      onChange={event => setSignerName(event.target.value)}
                      placeholder="Type your full legal name to sign"
                    />
                  </div>
                  <label className="flex items-start gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={accepted}
                      onChange={event => setAccepted(event.target.checked)}
                      className="mt-1 h-4 w-4"
                    />
                    <span>
                      I have read and agree to this waiver for myself or the attendee listed above.
                    </span>
                  </label>
                  <Button
                    disabled={
                      !effectiveSignerName.trim() ||
                      !effectiveSignerEmail.trim() ||
                      !effectiveSignerPhone.trim() ||
                      !effectiveEmergencyContactName.trim() ||
                      !effectiveEmergencyContactPhone.trim() ||
                      !signatureDate ||
                      !ageConfirmed ||
                      mediaReleaseConsent === null ||
                      !accepted ||
                      signMutation.isPending
                    }
                    onClick={() => signMutation.mutate()}
                  >
                    {signMutation.isPending ? 'Signing...' : 'Sign Waiver'}
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </PublicShell>
  );
}
